// Agreement-gate bounces — the SPA half of the two acceptance gates.
//
// Split out of apiClient.ts when #3287's confirmation step pushed that file
// past the 1000-line hard limit. Cohesive on its own terms: both gates share
// the latch-once / invalidate-cache / soft-navigate-with-hard-fallback shape,
// and both now sit next to the prompter they ask through
// (`agreementGatePrompt.ts`). apiClient.ts calls these; nothing else does.

import { requestAgreementConsent } from '@/utils/agreementGatePrompt'

// --- Databehandleravtale gate (dpa_required) ---
//
// Same shape as apiClient's workspace-mismatch redirect: a mid-session gate
// flip 403s several parallel data calls at once, and only the first may
// drive navigation. Two differences:
//
//   - the cached gate status is dropped first. It is the reason the
//     operator wasn't already bounced: the session loaded it BEFORE the
//     version bump, so it still reads unlocked=true. Without the
//     invalidation DpaAcceptView re-reads that stale status on mount,
//     concludes there is nothing to accept, and sends the operator
//     straight back to the broken dashboard.
//   - navigation goes through the router (soft) rather than
//     window.location, so in-flight view state survives the bounce. The
//     router is dynamically imported for the same reason
//     invalidateAdminIdentity dynamic-imports useCurrentAdmin: a static
//     import would close a cycle (router → composables → apiClient).
//
// The handoff and accept screens are exempt — the accept screen calls gated
// endpoints on mount, and bouncing either screen onto itself is a loop.
const DPA_ACCEPT_ROUTE = '/dpa-required'
const DPA_ACCEPT_SCREEN_ROUTE = '/dpa-accept'

let dpaRedirectLatch = false

// Exposed for tests — the latch is module state and must not bleed
// between cases.
export function resetDpaRedirectLatch(): void {
  dpaRedirectLatch = false
}

// Resolves true when the bounce is under way (or already was), false when the
// operator declined the confirmation. The caller awaits it so it knows which
// error to reject with — see the call site.
export async function redirectToDpaAccept(): Promise<boolean> {
  if (dpaRedirectLatch) return true
  if (
    window.location.pathname === DPA_ACCEPT_ROUTE ||
    window.location.pathname === DPA_ACCEPT_SCREEN_ROUTE
  ) {
    return true
  }
  dpaRedirectLatch = true
  // Ask before bouncing (#3287). Every route behind this gate is a POST the
  // operator explicitly triggered, so an unannounced full-SPA replace reads as
  // coercive rather than as a missing step. On decline the latch is released so
  // a later attempt prompts again instead of failing silently.
  if (!(await requestAgreementConsent('dpa'))) {
    dpaRedirectLatch = false
    return false
  }
  void (async () => {
    try {
      const gate = await import('@/composables/useDpaGate')
      gate.invalidateDpaStatus()
      const mod = await import('@/router')
      const next = `${window.location.pathname}${window.location.search}`
      await mod.default.replace({ path: DPA_ACCEPT_ROUTE, query: { next } })
    } catch {
      // Best-effort: if the router module can't be reached (or the
      // navigation is cancelled by a concurrent one), fall back to a hard
      // navigation. The gate is blocking either way — the backend keeps
      // refusing every data call until the operator accepts.
      window.location.href = DPA_ACCEPT_ROUTE
    }
  })()
  return true
}

// --- Operatøravtale gate (terms_required) ---
//
// Payment-specific redirect with its own latch and cache invalidation. The
// backend only returns terms_required for payment activation/actions, so this
// sends the owner to the operator agreement and then resumes that action.
const TERMS_ACCEPT_ROUTE =
  '/dpa-accept?termsOnly=1&next=%2Fdashboard%2Fsettings%3Ftab%3Dpayments%26connect%3D1'

let termsRedirectLatch = false

// Exposed for tests — the latch is module state and must not bleed between
// cases.
export function resetTermsRedirectLatch(): void {
  termsRedirectLatch = false
}

// Same true/false contract as redirectToDpaAccept above.
export async function redirectToTermsAccept(): Promise<boolean> {
  if (termsRedirectLatch) return true
  if (window.location.pathname === '/dpa-accept') return true
  termsRedirectLatch = true
  // Same confirmation step as the DPA bounce above (#3287) — this is the
  // "Oppgrader til Team" case the issue was filed for.
  if (!(await requestAgreementConsent('terms'))) {
    termsRedirectLatch = false
    return false
  }
  void (async () => {
    try {
      const gate = await import('@/composables/useTermsGate')
      gate.invalidateTermsStatus()
      const mod = await import('@/router')
      await mod.default.replace(TERMS_ACCEPT_ROUTE)
    } catch {
      // Best-effort, same as the DPA path: payment activation remains refused
      // until the operator accepts.
      window.location.href = TERMS_ACCEPT_ROUTE
    }
  })()
  return true
}
